/**
 * Fallback Manager
 * 
 * Manages fallback to polling mode when WebSocket is unavailable.
 * Automatically switches back to WebSocket when connection is restored.
 */

import { TaskStatus } from './types';
import { wsLogger } from './logger';

/**
 * Polling configuration
 */
interface PollingConfig {
  interval: number;
  maxRetries: number;
  enabled: boolean;
}

/**
 * Task polling state
 */
interface TaskPollingState {
  taskId: string;
  intervalId: NodeJS.Timeout;
  retryCount: number;
  lastStatus: TaskStatus | null;
}

/**
 * Fallback Manager class
 */
export class FallbackManager {
  private isActive: boolean = false;
  private pollingTasks: Map<string, TaskPollingState> = new Map();
  private config: PollingConfig;
  private onStatusUpdate: ((taskId: string, status: TaskStatus) => void) | null = null;
  private onProgressUpdate: ((taskId: string, progress: number) => void) | null = null;

  constructor(config?: Partial<PollingConfig>) {
    this.config = {
      interval: config?.interval || 3000, // 3 seconds default
      maxRetries: config?.maxRetries || 10,
      enabled: config?.enabled ?? true
    };
  }

  /**
   * Check if fallback should be activated
   */
  shouldActivateFallback(failedAttempts: number): boolean {
    return this.config.enabled && failedAttempts >= 3;
  }

  /**
   * Activate polling mode
   */
  activatePolling(): void {
    if (this.isActive) {
      wsLogger.debug('Fallback', 'Polling already active');
      return;
    }

    wsLogger.logFallbackActivated();
    this.isActive = true;

    // Log mode transition
    this.logModeTransition('websocket', 'polling');
  }

  /**
   * Deactivate polling mode
   */
  deactivatePolling(): void {
    if (!this.isActive) {
      return;
    }

    wsLogger.logFallbackDeactivated();
    this.isActive = false;

    // Stop all polling tasks
    this.stopAllPolling();

    // Log mode transition
    this.logModeTransition('polling', 'websocket');
  }

  /**
   * Check if WebSocket is available
   */
  async checkWebSocketAvailability(wsUrl: string): Promise<boolean> {
    try {
      // Try to establish a test connection
      const response = await fetch(wsUrl.replace('ws://', 'http://').replace('wss://', 'https://') + '/health', {
        method: 'GET',
        timeout: 5000
      } as any);

      return response.ok;
    } catch (error) {
      wsLogger.warn('Fallback', 'WebSocket availability check failed', { error });
      return false;
    }
  }

  /**
   * Get current mode
   */
  getCurrentMode(): 'websocket' | 'polling' {
    return this.isActive ? 'polling' : 'websocket';
  }

  /**
   * Check if polling is active
   */
  isPollingActive(): boolean {
    return this.isActive;
  }

  /**
   * Start polling for a task
   */
  startTaskPolling(
    taskId: string,
    fetchStatus: () => Promise<{ status: TaskStatus; progress?: number }>
  ): void {
    if (!this.isActive) {
      wsLogger.warn('Fallback', 'Cannot start polling: Fallback not active');
      return;
    }

    // Stop existing polling for this task
    this.stopTaskPolling(taskId);

    wsLogger.debug('Fallback', `Starting polling for task: ${taskId}`);

    // Create polling interval
    const intervalId = setInterval(async () => {
      const state = this.pollingTasks.get(taskId);
      if (!state) return;

      try {
        const result = await fetchStatus();

        // Check if status changed
        if (result.status !== state.lastStatus) {
          state.lastStatus = result.status;
          this.onStatusUpdate?.(taskId, result.status);
        }

        // Update progress if available
        if (result.progress !== undefined) {
          this.onProgressUpdate?.(taskId, result.progress);
        }

        // Stop polling if task is completed or failed
        if (result.status === TaskStatus.COMPLETED || result.status === TaskStatus.FAILED) {
          wsLogger.debug('Fallback', `Task ${taskId} finished, stopping polling`);
          this.stopTaskPolling(taskId);
        }

        // Reset retry count on success
        state.retryCount = 0;
      } catch (error) {
        wsLogger.error('Fallback', `Polling error for task ${taskId}`, { error });

        // Increment retry count
        state.retryCount++;

        // Stop polling if max retries reached
        if (state.retryCount >= this.config.maxRetries) {
          wsLogger.error('Fallback', `Max retries reached for task ${taskId}, stopping polling`);
          this.stopTaskPolling(taskId);
        }
      }
    }, this.config.interval);

    // Store polling state
    this.pollingTasks.set(taskId, {
      taskId,
      intervalId,
      retryCount: 0,
      lastStatus: null
    });
  }

  /**
   * Stop polling for a task
   */
  stopTaskPolling(taskId: string): void {
    const state = this.pollingTasks.get(taskId);
    if (state) {
      clearInterval(state.intervalId);
      this.pollingTasks.delete(taskId);
      wsLogger.debug('Fallback', `Stopped polling for task: ${taskId}`);
    }
  }

  /**
   * Stop all polling tasks
   */
  stopAllPolling(): void {
    wsLogger.debug('Fallback', `Stopping all polling tasks (${this.pollingTasks.size} active)`);

    for (const [taskId, state] of this.pollingTasks.entries()) {
      clearInterval(state.intervalId);
    }

    this.pollingTasks.clear();
  }

  /**
   * Register status update callback
   */
  onTaskStatusUpdate(callback: (taskId: string, status: TaskStatus) => void): void {
    this.onStatusUpdate = callback;
  }

  /**
   * Register progress update callback
   */
  onTaskProgressUpdate(callback: (taskId: string, progress: number) => void): void {
    this.onProgressUpdate = callback;
  }

  /**
   * Get polling statistics
   */
  getPollingStats(): {
    isActive: boolean;
    activeTaskCount: number;
    pollingInterval: number;
    mode: 'websocket' | 'polling';
  } {
    return {
      isActive: this.isActive,
      activeTaskCount: this.pollingTasks.size,
      pollingInterval: this.config.interval,
      mode: this.getCurrentMode()
    };
  }

  /**
   * Log mode transition
   */
  private logModeTransition(from: string, to: string): void {
    wsLogger.logModeTransition(from, to);
  }

  /**
   * Update polling configuration
   */
  updateConfig(config: Partial<PollingConfig>): void {
    this.config = {
      ...this.config,
      ...config
    };

    wsLogger.debug('Fallback', 'Configuration updated', this.config);
  }

  /**
   * Cleanup
   */
  cleanup(): void {
    this.stopAllPolling();
    this.isActive = false;
    this.onStatusUpdate = null;
    this.onProgressUpdate = null;
  }
}

/**
 * Create default fallback manager
 */
export function createFallbackManager(config?: Partial<PollingConfig>): FallbackManager {
  return new FallbackManager(config);
}
